import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { ApiList, ApiSchedule } from '../../types';
import { AddressSummary } from '../Form/AddressSummary';
import { Button } from '../Form/Button';
import { renderTemplate } from '../utils';
import './DraftSteps.css';
import { formatScheduleText } from './utils';
import { WizardStep, canActivate, deliveryAddressOf, whatToDoNext } from './wizard';

type DraftStepsProps = {
	list: ApiList;
	schedule?: ApiSchedule;
	loading?: boolean;
	onRename: () => void;
	onStep: (step: WizardStep) => void;
	onReview: () => void;
};

/**
 * A subscription that is not running yet, shown as the five things it is made of: what it is
 * called, when it goes, where it goes, what is in it, and the look at all four before it starts.
 * Each one says what it is waiting for, or what has been chosen, and opens the same dialog the
 * finished card uses for it.
 */
export const DraftSteps = ({ list, schedule, loading, onRename, onStep, onReview }: DraftStepsProps): JSX.Element => {
	const { locale } = useConfig();
	const translations = useTranslation();
	const t = { ...translations.subscriptions, form: translations.form };
	const address = deliveryAddressOf(schedule);
	const packages = list.items.reduce((total, item) => total + item.quantity, 0);
	const next = whatToDoNext(list, schedule);
	const ready = canActivate(list, schedule);

	const steps: {
		step?: WizardStep;
		title: string;
		done: boolean;
		said: JSX.Element | string;
		action: string;
		onAction: () => void;
		asLink?: boolean;
		/* the step cannot be done yet, because another one holds what it would be saved on */
		waiting?: boolean;
	}[] = [
		{
			title: t.stepName,
			done: true,
			said: list.name,
			action: t.change,
			onAction: onRename,
			asLink: true,
		},
		{
			step: 'schedule',
			title: t.stepSchedule,
			done: Boolean(schedule),
			// what it says once it is chosen is the sentence the finished card says
			said: schedule ? formatScheduleText(schedule, locale, t) : t.stepScheduleHelp,
			action: t.schedule.createSchedule,
			onAction: () => onStep('schedule'),
		},
		{
			step: 'address',
			title: t.stepAddress,
			done: Boolean(address),
			/*
			 * The address is a column on the schedule, so there is nowhere to keep one until the
			 * interval has been chosen - and an address picked before that would quietly be dropped.
			 */
			waiting: !schedule,
			said: address ? (
				<AddressSummary address={address} />
			) : schedule ? (
				t.stepAddressHelp
			) : (
				t.stepAddressWaitsForSchedule
			),
			action: t.form.selectDeliveryAddress,
			onAction: () => onStep('address'),
		},
		{
			step: 'products',
			title: t.stepProducts,
			done: list.items.length > 0,
			said: list.items.length
				? renderTemplate(t.productsSummary, { products: list.items.length, packages })
				: t.stepProductsHelp,
			action: t.chooseProducts,
			onAction: () => onStep('products'),
		},
	];

	return (
		<ol className="subscription-steps">
			{steps.map((row, index) => (
				<li className={`subscription-step${row.done ? ' done' : ''}`} key={row.title}>
					<span className="step-marker" aria-hidden="true">
						{row.done ? '✓' : index + 1}
					</span>
					<div className="step-said">
						<h3>
							{index + 1}. {row.title}
						</h3>
						<div className="step-value">{row.said}</div>
					</div>
					{row.asLink ? (
						<button type="button" className="text-link step-action" onClick={row.onAction}>
							{row.action}
						</button>
					) : (
						<Button
							className={`small step-action${next === row.step ? '' : ' secondary'}`}
							disabled={loading || row.waiting}
							onClick={row.onAction}
						>
							{row.action}
						</Button>
					)}
				</li>
			))}
			<li className="subscription-step">
				<span className="step-marker" aria-hidden="true">
					{steps.length + 1}
				</span>
				<div className="step-said">
					<h3>
						{steps.length + 1}. {t.reviewAndActivate}
					</h3>
					<div className="step-value">{ready ? t.stepReviewHelp : t.stepReviewWaiting}</div>
				</div>
				<Button
					className={`small step-action${ready ? '' : ' secondary'}`}
					disabled={!ready || loading}
					onClick={onReview}
				>
					{t.reviewAndActivate}
				</Button>
			</li>
		</ol>
	);
};
